import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Field, Input, Sticker } from '@/components/ui';
import { getDraft, saveDraft, isAdult } from '@/features/onboarding/store';

function Check({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-inset border-2 border-hairline-strong p-3 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-5 shrink-0 accent-accent"
      />
      <span className="text-ink-soft">{children}</span>
    </label>
  );
}

export function OnboardingTermsPage() {
  const navigate = useNavigate();
  const d = getDraft();
  const [dob, setDob] = useState(d.dob);
  const [age18, setAge18] = useState(d.acceptedAge18);
  const [terms, setTerms] = useState(d.acceptedTerms);
  const [loc, setLoc] = useState(d.acceptedLocation);
  const [err, setErr] = useState<string | null>(null);

  const adult = isAdult(dob);
  const ready = adult && age18 && terms && loc;

  const submit = () => {
    if (!dob) return setErr('Enter your date of birth.');
    if (!adult) return setErr('You must be 18 or older to use Nearmirer.');
    if (!ready) return setErr('Please check all three boxes.');
    saveDraft({ dob, acceptedAge18: age18, acceptedTerms: terms, acceptedLocation: loc });
    void navigate({ to: '/onboarding/how-to' });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Sticker flavor="berry" rotate={-3}>
          one-time setup · 1 / 4
        </Sticker>
        <h1 className="mt-4 text-3xl font-semibold">The ground rules</h1>
        <p className="mt-2 text-sm text-ink-soft">
          Nearmirer is 18+. We ask once, and again only if these documents change.
        </p>
      </div>

      <Field
        label="Date of birth"
        hint="We use this only to confirm you're 18+. It is not shown to anyone."
        error={dob && !adult ? 'You must be at least 18.' : undefined}
      >
        <Input type="date" value={dob} max="2010-01-01" onChange={(e) => setDob(e.target.value)} />
      </Field>

      <div className="flex flex-col gap-2">
        <Check checked={age18} onChange={setAge18}>
          I confirm I am at least 18 years old.
        </Check>
        <Check checked={terms} onChange={setTerms}>
          I have read and agree to the{' '}
          <Link to="/terms" className="font-semibold underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/community-guidelines" className="font-semibold underline">
            Community Guidelines
          </Link>
          .
        </Check>
        <Check checked={loc} onChange={setLoc}>
          I consent to Nearmirer using my precise device location while I use the app, as described
          in the{' '}
          <Link to="/privacy" className="font-semibold underline">
            Privacy Policy
          </Link>
          . I can withdraw this anytime in my device settings.
        </Check>
      </div>

      {err && <p className="text-sm text-critical">{err}</p>}

      <Button variant="candy" size="lg" onClick={submit} disabled={!ready}>
        Agree & continue
      </Button>
    </div>
  );
}
